<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Pharmacy Inventory</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('pharmacy-dashboard')">Pharmacy</a></li>
                      <li class="breadcrumb-item active">Inventory</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Stats Cards -->
            <div class="row">
              <!-- Total Items -->
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Total Items</p>
                        <h4 class="mb-0">{{ stats.totalItems || 0 }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-primary">
                          <span class="avatar-title"><i class="mdi mdi-pill font-size-24"></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Low Stock -->
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid" :class="{'border-warning': stats.lowStock > 0}">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Low Stock</p>
                        <h4 class="mb-0" :class="{'text-warning': stats.lowStock > 0}">{{ stats.lowStock || 0 }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-warning">
                          <span class="avatar-title bg-warning"><i class="mdi mdi-alert font-size-24"></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Out of Stock -->
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid" :class="{'border-danger': stats.outOfStock > 0}">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Out of Stock</p>
                        <h4 class="mb-0" :class="{'text-danger': stats.outOfStock > 0}">{{ stats.outOfStock || 0 }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-danger">
                          <span class="avatar-title bg-danger"><i class="mdi mdi-close-circle font-size-24"></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Expiring Soon -->
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid" :class="{'border-info': stats.expiringSoon > 0}">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Expiring (30 days)</p>
                        <h4 class="mb-0" :class="{'text-info': stats.expiringSoon > 0}">{{ stats.expiringSoon || 0 }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-info">
                          <span class="avatar-title bg-info"><i class="mdi mdi-calendar-clock font-size-24"></i></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Actions & Filters -->
            <div class="row mb-3">
              <div class="col-lg-8">
                <div class="d-flex flex-wrap gap-2">
                  <button class="btn btn-primary" @click="openReceiveStockModal">
                    <i class="mdi mdi-plus me-1"></i> Receive Stock
                  </button>
                  <button class="btn btn-soft-warning" @click="openAdjustmentModal">
                    <i class="mdi mdi-swap-horizontal me-1"></i> Adjustment
                  </button>
                  <button class="btn btn-soft-info" @click="goTo('stock-movements')">
                    <i class="mdi mdi-history me-1"></i> Movement History
                  </button>
                  <button class="btn btn-soft-success" @click="goTo('drug-catalog')">
                    <i class="mdi mdi-pill me-1"></i> Drug Catalog
                  </button>
                  <button class="btn btn-soft-secondary" @click="exportInventory">
                    <i class="mdi mdi-download me-1"></i> Export
                  </button>
                </div>
              </div>
              <div class="col-lg-4 text-end">
                <button class="btn btn-light" @click="loadInventory" :disabled="loading">
                  <i class="mdi mdi-refresh" :class="{'mdi-spin': loading}"></i> Refresh
                </button>
              </div>
            </div>

            <!-- Main Inventory Card -->
            <div class="card">
              <div class="card-body">
                <!-- Filters -->
                <div class="row mb-4">
                  <div class="col-md-4">
                    <div class="input-group">
                      <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
                      <input type="text" class="form-control" v-model="search"
                             placeholder="Search drug name, code..." @input="filterInventory">
                    </div>
                  </div>
                  <div class="col-md-2">
                    <select class="form-select" v-model="filterCategory" @change="filterInventory">
                      <option value="">All Categories</option>
                      <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
                    </select>
                  </div>
                  <div class="col-md-2">
                    <select class="form-select" v-model="filterStatus" @change="filterInventory">
                      <option value="">All Status</option>
                      <option value="In Stock">In Stock</option>
                      <option value="Low Stock">Low Stock</option>
                      <option value="Out of Stock">Out of Stock</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>
                  <div class="col-md-2">
                    <select class="form-select" v-model="filterForm" @change="filterInventory">
                      <option value="">All Forms</option>
                      <option v-for="form in drugForms" :key="form" :value="form">{{ form }}</option>
                    </select>
                  </div>
                  <div class="col-md-2">
                    <select class="form-select" v-model="sortBy" @change="sortInventory">
                      <option value="drugName">Name A-Z</option>
                      <option value="quantityInStock">Stock Level</option>
                      <option value="expiryDate">Expiry Date</option>
                      <option value="category">Category</option>
                    </select>
                  </div>
                </div>

                <!-- Loading -->
                <div v-if="loading" class="text-center py-5">
                  <div class="spinner-border text-primary mb-3"></div>
                  <p class="text-muted">Loading inventory...</p>
                </div>

                <!-- Inventory Table -->
                <div v-else class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="table-light">
                    <tr>
                      <th style="width: 50px;">#</th>
                      <th>Drug Name</th>
                      <th>Category</th>
                      <th>Form / Strength</th>
                      <th>Batch No.</th>
                      <th class="text-center">In Stock</th>
                      <th>Expiry Date</th>
                      <th>Status</th>
                      <th class="text-end">Selling Price</th>
                      <th style="width: 120px;">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, index) in filteredInventory" :key="item.inventoryID"
                        :class="getRowClass(item)">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <div>
                          <h6 class="mb-0">{{ item.drugName }}</h6>
                          <small class="text-muted">{{ item.genericName }}</small>
                        </div>
                      </td>
                      <td><span class="badge bg-light text-dark">{{ item.category || '-' }}</span></td>
                      <td>
                        <span>{{ item.drugForm }}</span>
                        <small v-if="item.strength" class="text-muted d-block">{{ item.strength }}</small>
                      </td>
                      <td><code>{{ item.batchNumber || '-' }}</code></td>
                      <td class="text-center">
                            <span class="fw-bold" :class="getStockClass(item)">
                              {{ item.quantityInStock }}
                            </span>
                        <small class="text-muted d-block">{{ item.unitOfMeasure }}</small>
                      </td>
                      <td>
                            <span :class="getExpiryClass(item.expiryDate)">
                              {{ formatDate(item.expiryDate) }}
                            </span>
                        <small v-if="getDaysToExpiry(item.expiryDate) <= 30 && getDaysToExpiry(item.expiryDate) > 0"
                               class="text-warning d-block">
                          {{ getDaysToExpiry(item.expiryDate) }} days left
                        </small>
                      </td>
                      <td>
                            <span class="badge" :class="getStockStatusBadge(item)">
                              {{ getStockStatus(item) }}
                            </span>
                      </td>
                      <td class="text-end">KES {{ numberFormat(item.sellingPrice) }}</td>
                      <td>
                        <div class="btn-group btn-group-sm">
                          <button class="btn btn-soft-primary" @click="viewItem(item)" title="View Details">
                            <i class="mdi mdi-eye"></i>
                          </button>
                          <button class="btn btn-soft-warning" @click="adjustStock(item)" title="Adjust Stock">
                            <i class="mdi mdi-pencil"></i>
                          </button>
                          <button class="btn btn-soft-info" @click="viewMovements(item)" title="View Movements">
                            <i class="mdi mdi-history"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="filteredInventory.length === 0">
                      <td colspan="10" class="text-center py-5">
                        <i class="mdi mdi-pill-off font-size-48 text-muted d-block mb-3"></i>
                        <p class="text-muted mb-0">No inventory items found</p>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-4">
                  <div class="text-muted">
                    Showing {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalItems) }} of {{ totalItems }}
                  </div>
                  <nav>
                    <ul class="pagination mb-0">
                      <li class="page-item" :class="{ disabled: currentPage === 1 }">
                        <a class="page-link" href="javascript:void(0);" @click="goToPage(currentPage - 1)">
                          <i class="mdi mdi-chevron-left"></i>
                        </a>
                      </li>
                      <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                        <a class="page-link" href="javascript:void(0);" @click="goToPage(page)">{{ page }}</a>
                      </li>
                      <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                        <a class="page-link" href="javascript:void(0);" @click="goToPage(currentPage + 1)">
                          <i class="mdi mdi-chevron-right"></i>
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>

            <!-- Alerts Section -->
            <div class="row" v-if="lowStockItems.length > 0 || expiringItems.length > 0">
              <!-- Low Stock Alert -->
              <div class="col-lg-6" v-if="lowStockItems.length > 0">
                <div class="card border-warning">
                  <div class="card-header bg-warning bg-opacity-10">
                    <h5 class="mb-0 text-warning">
                      <i class="mdi mdi-alert me-2"></i>Low Stock Alert ({{ lowStockItems.length }})
                    </h5>
                  </div>
                  <div class="card-body p-0">
                    <div class="table-responsive" style="max-height: 300px;">
                      <table class="table table-sm mb-0">
                        <thead class="table-light sticky-top">
                        <tr>
                          <th>Drug</th>
                          <th class="text-center">In Stock</th>
                          <th class="text-center">Reorder Level</th>
                          <th>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="item in lowStockItems.slice(0, 10)" :key="item.inventoryID">
                          <td>
                            <span class="fw-medium">{{ item.drugName }}</span>
                            <small class="text-muted d-block">{{ item.drugForm }}</small>
                          </td>
                          <td class="text-center text-warning fw-bold">{{ item.quantityInStock }}</td>
                          <td class="text-center">{{ item.reorderLevel }}</td>
                          <td>
                            <button class="btn btn-sm btn-outline-primary" @click="quickReorder(item)">
                              Reorder
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Expiring Soon Alert -->
              <div class="col-lg-6" v-if="expiringItems.length > 0">
                <div class="card border-info">
                  <div class="card-header bg-info bg-opacity-10">
                    <h5 class="mb-0 text-info">
                      <i class="mdi mdi-calendar-clock me-2"></i>Expiring Soon ({{ expiringItems.length }})
                    </h5>
                  </div>
                  <div class="card-body p-0">
                    <div class="table-responsive" style="max-height: 300px;">
                      <table class="table table-sm mb-0">
                        <thead class="table-light sticky-top">
                        <tr>
                          <th>Drug</th>
                          <th>Batch</th>
                          <th>Expiry</th>
                          <th class="text-center">Qty</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="item in expiringItems.slice(0, 10)" :key="item.inventoryID"
                            :class="{'table-danger': getDaysToExpiry(item.expiryDate) <= 7}">
                          <td>
                            <span class="fw-medium">{{ item.drugName }}</span>
                          </td>
                          <td><code>{{ item.batchNumber }}</code></td>
                          <td>
                                <span :class="getDaysToExpiry(item.expiryDate) <= 7 ? 'text-danger' : 'text-warning'">
                                  {{ formatDate(item.expiryDate) }}
                                </span>
                            <small class="d-block">{{ getDaysToExpiry(item.expiryDate) }} days</small>
                          </td>
                          <td class="text-center">{{ item.quantityInStock }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- View Item Modal -->
    <div class="modal fade" id="viewItemModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="mdi mdi-pill me-2 text-primary"></i>Item Details</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="selectedItem">
            <div class="row">
              <div class="col-md-6">
                <table class="table table-sm table-borderless">
                  <tr>
                    <td class="text-muted">Drug Name:</td>
                    <td class="fw-medium">{{ selectedItem.drugName }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Generic Name:</td>
                    <td>{{ selectedItem.genericName || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Brand Name:</td>
                    <td>{{ selectedItem.brandName || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Category:</td>
                    <td>{{ selectedItem.category || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Drug Form:</td>
                    <td>{{ selectedItem.drugForm || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Strength:</td>
                    <td>{{ selectedItem.strength || '-' }}</td>
                  </tr>
                </table>
              </div>
              <div class="col-md-6">
                <table class="table table-sm table-borderless">
                  <tr>
                    <td class="text-muted">Batch Number:</td>
                    <td><code>{{ selectedItem.batchNumber || '-' }}</code></td>
                  </tr>
                  <tr>
                    <td class="text-muted">Expiry Date:</td>
                    <td :class="getExpiryClass(selectedItem.expiryDate)">{{ formatDate(selectedItem.expiryDate) }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Quantity In Stock:</td>
                    <td class="fw-bold" :class="getStockClass(selectedItem)">{{ selectedItem.quantityInStock }} {{ selectedItem.unitOfMeasure }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Reorder Level:</td>
                    <td>{{ selectedItem.reorderLevel }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Purchase Price:</td>
                    <td>KES {{ numberFormat(selectedItem.purchasePrice) }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Selling Price:</td>
                    <td class="fw-bold text-success">KES {{ numberFormat(selectedItem.sellingPrice) }}</td>
                  </tr>
                </table>
              </div>
            </div>
            <hr>
            <div class="row">
              <div class="col-md-6">
                <p class="mb-1"><span class="text-muted">Supplier:</span> {{ selectedItem.supplier || '-' }}</p>
                <p class="mb-1"><span class="text-muted">Storage Location:</span> {{ selectedItem.storageLocation || '-' }}</p>
              </div>
              <div class="col-md-6">
                <p class="mb-1"><span class="text-muted">Last Restock:</span> {{ formatDate(selectedItem.lastRestockDate) }}</p>
                <p class="mb-1"><span class="text-muted">Qty Received:</span> {{ selectedItem.quantityReceived }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Close</button>
            <button type="button" class="btn btn-warning" @click="adjustStock(selectedItem)" data-bs-dismiss="modal">
              <i class="mdi mdi-pencil me-1"></i> Adjust Stock
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Adjust Modal -->
    <div class="modal fade" id="quickAdjustModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="mdi mdi-swap-horizontal me-2 text-warning"></i>Quick Stock Adjustment</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="adjustItem">
            <div class="alert alert-light mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ adjustItem.drugName }}</strong>
                  <br><small class="text-muted">Batch: {{ adjustItem.batchNumber }} | Current Stock: <span class="fw-bold">{{ adjustItem.quantityInStock }}</span> {{ adjustItem.unitOfMeasure }}</small>
                </div>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="adjustItem = null">
                  <i class="mdi mdi-close"></i> Change
                </button>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Adjustment Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="adjustment.type">
                <option value="">Select Type</option>
                <option value="ADD">Add Stock</option>
                <option value="REMOVE">Remove Stock</option>
                <option value="DAMAGED">Mark as Damaged</option>
                <option value="EXPIRED">Mark as Expired</option>
                <option value="CORRECTION">Stock Correction</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Quantity <span class="text-danger">*</span></label>
              <input type="number" class="form-control" v-model="adjustment.quantity" min="1">
            </div>

            <div class="mb-3">
              <label class="form-label">Reason <span class="text-danger">*</span></label>
              <textarea class="form-control" rows="2" v-model="adjustment.reason" placeholder="Enter reason for adjustment..."></textarea>
            </div>

            <div class="alert alert-info mb-0" v-if="adjustment.type && adjustment.quantity">
              <i class="mdi mdi-information me-1"></i>
              New Stock Level: <strong>{{ calculateNewStock() }}</strong> {{ adjustItem.unitOfMeasure }}
            </div>
          </div>
          <div class="modal-body" v-else>
            <div class="mb-3">
              <label class="form-label">Select Item to Adjust <span class="text-danger">*</span></label>
              <select class="form-select" v-model="selectedAdjustItemID" @change="selectAdjustItem">
                <option value="">-- Select an item --</option>
                <option v-for="item in inventory" :key="item.inventoryID" :value="item.inventoryID">
                  {{ item.drugName }} - {{ item.batchNumber }} (Stock: {{ item.quantityInStock }})
                </option>
              </select>
            </div>
            <div class="text-center py-3 text-muted">
              <i class="mdi mdi-arrow-up font-size-24 d-block mb-2"></i>
              <small>Select an item from the dropdown above, or close this and click the <i class="mdi mdi-pencil"></i> button on a row.</small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-warning" @click="submitAdjustment"
                    :disabled="!adjustItem || !adjustment.type || !adjustment.quantity || !adjustment.reason || adjusting">
              <span v-if="adjusting"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
              <span v-else><i class="mdi mdi-check me-1"></i> Save Adjustment</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "PharmacyInventory",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      loading: true,
      inventory: [],
      filteredInventory: [],

      // Stats
      stats: {
        totalItems: 0,
        lowStock: 0,
        outOfStock: 0,
        expiringSoon: 0
      },

      // Alerts
      lowStockItems: [],
      expiringItems: [],

      // Filters
      search: '',
      filterCategory: '',
      filterStatus: '',
      filterForm: '',
      sortBy: 'drugName',

      // Dropdown options
      categories: [],
      drugForms: [],

      // Pagination
      currentPage: 1,
      pageSize: 20,
      totalItems: 0,
      totalPages: 0,

      // Selected item
      selectedItem: null,
      adjustItem: null,
      selectedAdjustItemID: '',
      adjustment: {
        type: '',
        quantity: '',
        reason: ''
      },
      adjusting: false,

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: ''
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    numberFormat(num) {
      return Number(num || 0).toLocaleString();
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    getStockStatus(item) {
      const qty = Number(item.quantityInStock) || 0;
      const reorder = Number(item.reorderLevel) || 10;
      if (qty <= 0) return 'Out of Stock';
      if (qty <= reorder) return 'Low Stock';
      return 'In Stock';
    },

    getStockStatusBadge(item) {
      const status = this.getStockStatus(item);
      if (status === 'Out of Stock') return 'bg-danger';
      if (status === 'Low Stock') return 'bg-warning text-dark';
      return 'bg-success';
    },

    getStockClass(item) {
      const qty = Number(item.quantityInStock) || 0;
      const reorder = Number(item.reorderLevel) || 10;
      if (qty <= 0) return 'text-danger';
      if (qty <= reorder) return 'text-warning';
      return 'text-success';
    },

    getExpiryClass(date) {
      if (!date) return '';
      const days = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
      if (days <= 0) return 'text-danger fw-bold';
      if (days <= 30) return 'text-danger';
      if (days <= 90) return 'text-warning';
      return '';
    },

    getRowClass(item) {
      const qty = Number(item.quantityInStock) || 0;
      const reorder = Number(item.reorderLevel) || 10;
      if (qty <= 0) return 'table-danger';
      if (qty <= reorder) return 'table-warning';
      if (item.expiryDate) {
        const days = Math.ceil((new Date(item.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
        if (days <= 0) return 'table-danger';
        if (days <= 30) return 'table-warning';
      }
      return '';
    },

    getDaysToExpiry(date) {
      if (!date) return 999;
      return Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
    },

    // Load Inventory
    loadInventory() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getPharmacyInventory");
      data.append("facilityID", this.facilityID);
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);

      execute(data, hospital)
          .then((res) => {
            console.log('Inventory API Response:', res.data);
            if (res.data.success) {
              this.inventory = res.data.data.inventory || [];
              this.filteredInventory = [...this.inventory];
              this.totalItems = Number(res.data.data.count?.total) || this.inventory.length;
              this.totalPages = Math.ceil(this.totalItems / this.pageSize);
              try {
                this.calculateStats();
                this.extractFilters();
              } catch (e) {
                console.error('Error in calculateStats/extractFilters:', e);
              }
            } else {
              this.inventory = [];
              this.filteredInventory = [];
            }
          })
          .catch((e) => {
            console.error('API Error:', e);
          })
          .finally(() => {
            this.loading = false;
          });
    },

    calculateStats() {
      const today = new Date();
      const thirtyDaysLater = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

      this.stats.totalItems = this.inventory.length;
      this.stats.outOfStock = this.inventory.filter(i => !i.quantityInStock || Number(i.quantityInStock) <= 0).length;
      this.stats.lowStock = this.inventory.filter(i => Number(i.quantityInStock) > 0 && Number(i.quantityInStock) <= Number(i.reorderLevel || 10)).length;
      this.stats.expiringSoon = this.inventory.filter(i => {
        if (!i.expiryDate) return false;
        return new Date(i.expiryDate) <= thirtyDaysLater;
      }).length;

      this.lowStockItems = this.inventory.filter(i => Number(i.quantityInStock) > 0 && Number(i.quantityInStock) <= Number(i.reorderLevel || 10));
      this.expiringItems = this.inventory.filter(i => {
        if (!i.expiryDate) return false;
        return new Date(i.expiryDate) <= thirtyDaysLater;
      });
    },

    extractFilters() {
      const cats = new Set();
      const forms = new Set();
      this.inventory.forEach(item => {
        if (item.category) cats.add(item.category);
        if (item.drugForm) forms.add(item.drugForm);
      });
      this.categories = Array.from(cats).sort();
      this.drugForms = Array.from(forms).sort();
    },

    filterInventory() {
      let result = [...this.inventory];

      if (this.search) {
        const term = this.search.toLowerCase();
        result = result.filter(i =>
            (i.drugName && i.drugName.toLowerCase().includes(term)) ||
            (i.genericName && i.genericName.toLowerCase().includes(term)) ||
            (i.drugCode && i.drugCode.toLowerCase().includes(term)) ||
            (i.batchNumber && i.batchNumber.toLowerCase().includes(term))
        );
      }

      if (this.filterCategory) {
        result = result.filter(i => i.category === this.filterCategory);
      }

      if (this.filterStatus) {
        result = result.filter(i => this.getStockStatus(i) === this.filterStatus);
      }

      if (this.filterForm) {
        result = result.filter(i => i.drugForm === this.filterForm);
      }

      this.filteredInventory = result;
      this.sortInventory();
    },

    sortInventory() {
      this.filteredInventory.sort((a, b) => {
        if (this.sortBy === 'drugName') return (a.drugName || '').localeCompare(b.drugName || '');
        if (this.sortBy === 'quantityInStock') return Number(a.quantityInStock || 0) - Number(b.quantityInStock || 0);
        if (this.sortBy === 'expiryDate') return new Date(a.expiryDate) - new Date(b.expiryDate);
        if (this.sortBy === 'category') return (a.category || '').localeCompare(b.category || '');
        return 0;
      });
    },

    // View Item Details
    viewItem(item) {
      this.selectedItem = item;
      const modal = new bootstrap.Modal(document.getElementById('viewItemModal'));
      modal.show();
    },

    // Adjust Stock
    adjustStock(item) {
      this.adjustItem = item;
      this.selectedAdjustItemID = item.inventoryID;
      this.adjustment = { type: '', quantity: '', reason: '' };
      const modal = new bootstrap.Modal(document.getElementById('quickAdjustModal'));
      modal.show();
    },

    openAdjustmentModal() {
      this.adjustItem = null;
      this.selectedAdjustItemID = '';
      this.adjustment = { type: '', quantity: '', reason: '' };
      const modal = new bootstrap.Modal(document.getElementById('quickAdjustModal'));
      modal.show();
    },

    selectAdjustItem() {
      if (!this.selectedAdjustItemID) {
        this.adjustItem = null;
        return;
      }
      const item = this.inventory.find(i => i.inventoryID === this.selectedAdjustItemID);
      if (item) {
        this.adjustItem = item;
      }
    },

    calculateNewStock() {
      if (!this.adjustItem) return 0;
      const current = Number(this.adjustItem.quantityInStock) || 0;
      const qty = Number(this.adjustment.quantity) || 0;
      const type = this.adjustment.type?.toUpperCase();
      if (type === 'ADD') return current + qty;
      if (type === 'REMOVE' || type === 'DAMAGED' || type === 'EXPIRED') return Math.max(0, current - qty);
      if (type === 'CORRECTION') return qty; // Set to exact quantity
      return current;
    },

    submitAdjustment() {
      if (!this.adjustItem || !this.adjustment.type || !this.adjustment.quantity || !this.adjustment.reason) return;

      this.adjusting = true;
      const data = new FormData();
      data.append("function", "adjustInventory");
      data.append("facilityID", this.facilityID);
      data.append("inventoryID", this.adjustItem.inventoryID);
      data.append("drugID", this.adjustItem.drugID);
      data.append("batchNo", this.adjustItem.batchNumber);
      data.append("adjustmentType", this.adjustment.type);
      data.append("quantity", this.adjustment.quantity);
      data.append("reason", this.adjustment.reason);
      data.append("adjustedBy", this.sessionNames);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.adjusting = false;
            if (res.data.success) {
              this.$refs.toast.success('Stock adjusted successfully');
              bootstrap.Modal.getInstance(document.getElementById('quickAdjustModal')).hide();
              this.loadInventory();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to adjust stock');
            }
          })
          .catch((e) => {
            this.adjusting = false;
            this.$refs.toast.error('Error adjusting stock');
          });
    },

    // Open Receive Stock Modal
    openReceiveStockModal() {
      // Navigate to stock receive page or open modal
      this.goTo('stock-receive');
    },

    // Pagination
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.loadInventory();
    },

    // Export Inventory
    exportInventory() {
      let csv = 'Drug Name,Generic Name,Category,Form,Strength,Batch No,Quantity,Reorder Level,Expiry Date,Status,Purchase Price,Selling Price\n';

      this.filteredInventory.forEach(item => {
        csv += `"${item.drugName || ''}","${item.genericName || ''}","${item.category || ''}","${item.drugForm || ''}","${item.strength || ''}","${item.batchNumber || ''}","${item.quantityInStock || 0}","${item.reorderLevel || ''}","${this.formatDate(item.expiryDate)}","${this.getStockStatus(item)}","${item.purchasePrice || 0}","${item.sellingPrice || 0}"\n`;
      });

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pharmacy_inventory_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      this.$refs.toast.success('Inventory exported successfully');
    }
  },
  mounted() {
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';

    this.loadInventory();
  }
};
</script>

<style scoped>
.mini-stats-wid .card-body { padding: 20px; }
.mini-stat-icon { width: 48px; height: 48px; }
.avatar-sm { width: 3rem; height: 3rem; }
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
}

.btn-soft-warning {
  background-color: rgba(241, 180, 76, 0.1);
  color: #f1b44c;
  border: none;
}
.btn-soft-warning:hover { background-color: rgba(241, 180, 76, 0.2); }

.btn-soft-info {
  background-color: rgba(80, 165, 241, 0.1);
  color: #50a5f1;
  border: none;
}
.btn-soft-info:hover { background-color: rgba(80, 165, 241, 0.2); }

.btn-soft-success {
  background-color: rgba(52, 195, 143, 0.1);
  color: #34c38f;
  border: none;
}
.btn-soft-success:hover { background-color: rgba(52, 195, 143, 0.2); }

.btn-soft-secondary {
  background-color: rgba(116, 120, 141, 0.1);
  color: #74788d;
  border: none;
}
.btn-soft-secondary:hover { background-color: rgba(116, 120, 141, 0.2); }

.table > :not(caption) > * > * { padding: 0.6rem 0.75rem; }
</style>